import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
const SUPPORT_VARIANT = 50583957766363;
const ROOT = window.Shopify?.routes?.root || '/';
const OPT_OUT_KEY = 'grit-support-opt-out';
let busy = false;
let scheduled = false;
const cartUrl = (path) => `${ROOT}${path}`;
const isSupport = (item) => Number(item.variant_id || item.id) === SUPPORT_VARIANT;
async function getCart() {
  const response = await fetch(cartUrl('cart.js'), { credentials: 'same-origin' });
  if (!response.ok) throw new Error('カート情報を取得できませんでした。');
  return response.json();
}
function visibleCount(cart) {
  return cart.items.reduce((sum, item) => sum + (isSupport(item) ? 0 : item.quantity), 0);
}
function updateDisplay(cart, message = '') {
  const support = cart.items.find(isSupport);
  document.querySelectorAll('[data-grit-support-ui]').forEach((ui) => {
    const input = ui.querySelector('[data-grit-support-toggle]');
    const status = ui.querySelector('[data-grit-support-status]');
    if (input) {
      input.checked = !!support || (visibleCount(cart) > 0 && sessionStorage.getItem(OPT_OUT_KEY) !== '1');
      input.disabled = busy;
    }
    if (status) status.textContent = message;
  });
  const count = visibleCount(cart);
  document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(count, false));
  document.querySelectorAll('#cart-drawer .theme-drawer__badge').forEach((badge) => {
    badge.textContent = String(count);
    badge.classList.toggle('visually-hidden', count === 0);
  });
}
async function mutateCart(endpoint, payload, action, variantId = SUPPORT_VARIANT) {
  const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId).filter(Boolean))];
  const deferred = CartLinesUpdateEvent.createPromise();
  const lines = payload.items?.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })) ||
    [{ merchandiseId: String(variantId), quantity: endpoint === 'cart/add.js' ? 1 : 0 }];
  document.dispatchEvent(new CartLinesUpdateEvent({
    action, context: 'cart',
    lines,
    promise: deferred.promise,
  }));
  try {
    const response = await fetch(cartUrl(endpoint), {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || 'カートを更新できませんでした。');
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: result.sections, items: cart.items, itemCount: cart.item_count,
        source: 'grit-cart-support', didError: false },
    });
    updateDisplay(cart);
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}
async function reconcile() {
  if (busy) return;
  busy = true;
  const checkouts = [...document.querySelectorAll('#checkout')];
  checkouts.forEach((button) => { button.disabled = true; });
  try {
    let cart = await getCart();
    const merchandise = visibleCount(cart);
    const support = cart.items.find(isSupport);
    const optedOut = sessionStorage.getItem(OPT_OUT_KEY) === '1';
    updateDisplay(cart, merchandise && !support && !optedOut ? '料金を反映しています…' : '');
    if (merchandise && !support && !optedOut) {
      cart = await mutateCart('cart/add.js', { items: [{ id: SUPPORT_VARIANT, quantity: 1 }] }, 'update');
    } else if ((!merchandise || optedOut) && support) {
      cart = await mutateCart('cart/change.js', { id: support.key, quantity: 0 }, 'update');
    }
    updateDisplay(cart);
  } catch (error) {
    document.querySelectorAll('[data-grit-support-status]').forEach((status) => {
      status.textContent = error.message || '配送サポートを更新できませんでした。';
    });
  } finally {
    busy = false;
    checkouts.forEach((button) => { if (button.isConnected) button.disabled = false; });
    getCart().then((cart) => updateDisplay(cart)).catch(() => {});
  }
}
function scheduleReconcile() {
  if (scheduled || busy) return;
  scheduled = true;
  setTimeout(() => { scheduled = false; reconcile(); }, 100);
}
document.addEventListener('change', (event) => {
  if (!event.target.matches?.('[data-grit-support-toggle]')) return;
  sessionStorage.setItem(OPT_OUT_KEY, event.target.checked ? '0' : '1');
  scheduleReconcile();
});

function formatJPY(cents) {
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 })
    .format(cents / 100);
}

function updateDuoOffer(offer) {
  const form = offer.closest('form');
  const firstId = form?.querySelector('input[name="id"]')?.value;
  const secondSelect = offer.querySelector('[data-grit-second-variant]');
  if (!secondSelect) return;
  if (!offer._allSecondVariants) offer._allSecondVariants = [...secondSelect.options].map((option) => option.cloneNode(true));
  const first = offer._allSecondVariants.find((option) => option.value === firstId);
  const chosenProduct = offer.querySelector('input[name^="grit_second_product_"]:checked')?.value;
  if (chosenProduct) {
    const choices = offer._allSecondVariants.filter((option) => option.dataset.productId === chosenProduct);
    if (choices.length) {
      const previousId = secondSelect.value;
      secondSelect.replaceChildren(...choices.map((option) => option.cloneNode(true)));
      const matching = [...secondSelect.options].find((option) => option.value === previousId);
      secondSelect.value = matching ? previousId : choices[0].value;
    }
  }
  const second = secondSelect.selectedOptions?.[0];
  const firstPrice = Number(first?.dataset.price || 0);
  const secondPrice = Number(second?.dataset.price || 0);
  const duoSelected = offer.querySelector('input[name="grit_purchase_choice"]:checked')?.value === 'duo';
  offer.querySelector('[data-grit-second-variant-wrap]').hidden = !duoSelected;
  offer.querySelectorAll('[data-grit-duo-choice]').forEach((choice) => {
    choice.classList.toggle('grit-duo-offer__choice--selected', choice.dataset.gritDuoChoice === (duoSelected ? 'duo' : 'single'));
  });
  if (firstPrice && secondPrice) {
    offer.querySelector('[data-grit-single-price]').textContent = formatJPY(firstPrice);
    offer.querySelector('[data-grit-duo-price]').textContent = formatJPY(firstPrice + secondPrice - Math.round(Math.min(firstPrice, secondPrice) * 0.3));
  }
}

function initializeDuoOffers() {
  document.querySelectorAll('[data-grit-duo-offer]').forEach(updateDuoOffer);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeDuoOffers, { once: true });
} else initializeDuoOffers();

document.addEventListener('change', (event) => {
  const offer = event.target.closest?.('[data-grit-duo-offer]');
  if (offer) updateDuoOffer(offer);
  else if (event.target.closest?.('product-form-component')) {
    setTimeout(() => document.querySelectorAll('[data-grit-duo-offer]').forEach(updateDuoOffer), 50);
  }
});

window.addEventListener('click', async (event) => {
  const submitButton = event.target.closest?.('button[type="submit"]');
  const form = submitButton?.closest('form');
  const offer = form?.querySelector('[data-grit-duo-offer]');
  if (!offer || offer.querySelector('input[name="grit_purchase_choice"]:checked')?.value !== 'duo') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (busy) return;
  const firstId = Number(form.querySelector('input[name="id"]')?.value);
  const secondId = Number(offer.querySelector('[data-grit-second-variant]')?.value);
  if (!firstId || !secondId) return;
  const items = firstId === secondId
    ? [{ id: firstId, quantity: 2 }]
    : [{ id: firstId, quantity: 1 }, { id: secondId, quantity: 1 }];
  busy = true;
  try {
    await mutateCart('cart/add.js', { items }, 'add', firstId);
  } catch (error) {
    const status = offer.querySelector('[data-grit-duo-error]');
    if (status) status.textContent = error.message || 'カートに追加できませんでした。';
  } finally {
    busy = false;
    scheduleReconcile();
  }
}, true);

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('[data-grit-cart-recommendation]');
  if (!form) return;
  event.preventDefault();
  if (busy) return;
  const variantId = Number(form.querySelector('[name="id"]')?.value);
  if (!variantId) return;
  const button = form.querySelector('button[type="submit"]');
  busy = true;
  if (button) button.disabled = true;
  try {
    await mutateCart('cart/add.js', { items: [{ id: variantId, quantity: 1 }] }, 'add', variantId);
  } catch (error) {
    if (button) button.textContent = error.message || '追加できませんでした';
  } finally {
    busy = false;
    if (button?.isConnected) button.disabled = false;
    scheduleReconcile();
  }
});
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (busy) return;
  event.promise?.then(({ detail }) => {
    if (detail?.source !== 'grit-cart-support') scheduleReconcile();
  }).catch(() => {});
});
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleReconcile, { once: true });
} else scheduleReconcile();
